import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { onIdTokenChanged, signInWithPopup, signOut, type User } from 'firebase/auth';

import { auth, googleProvider } from '../firebase';

/**
 * Auth stāvoklis - Firebase Auth lietotājs + admin claim flag. Custom claim
 * `admin: true` jāiestata Firebase Console / REST API katram admin kontam
 * (sk. README sadaļā "Admin piekļuves piešķiršana"). Bez šī claim lietotājs
 * tiek pārvirzīts uz "Access denied" lapu, neraugoties uz veiksmīgu Google login.
 */
export type AuthState = {
    user: User | null;
    isAdmin: boolean;
    isLoading: boolean;
    signInWithGoogle: () => Promise<void>;
    signOut: () => Promise<void>;
    getIdToken: () => Promise<string | null>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = onIdTokenChanged(auth, async (fbUser) => {
            setUser(fbUser);
            if (fbUser) {
                // getIdTokenResult ar forceRefresh=false nolasa cache claims; ja tikko
                // piešķirts admin claim, vajadzīgs vienreiz forceRefresh, ko piedāvājam
                // pārlogošanās vai "Atjaunot tokenu" pogā.
                const tokenResult = await fbUser.getIdTokenResult();
                setIsAdmin(tokenResult.claims.admin === true);
            } else {
                setIsAdmin(false);
            }
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const value = useMemo<AuthState>(
        () => ({
            user,
            isAdmin,
            isLoading,
            signInWithGoogle: async () => {
                await signInWithPopup(auth, googleProvider);
            },
            signOut: async () => {
                await signOut(auth);
            },
            getIdToken: async () => (auth.currentUser ? auth.currentUser.getIdToken() : null),
        }),
        [user, isAdmin, isLoading],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth jālieto AuthProvider iekšienē');
    return ctx;
}
